/**
 * Canvas-mode grid renderer (BMC Pakket B, M5.5/M6.0).
 *
 * Wordt door Canvas.tsx gerenderd in plaats van het React-Flow-vlak
 * wanneer de actieve view-kind `layout_mode = "canvas"` heeft. De
 * renderer is bewust pure HTML + CSS-grid (geen React Flow): canvas-
 * views (BMC, later Lean Canvas / SWOT / etc.) kennen een vaste
 * 9-slot layout zónder pan/zoom, edges of vrije positionering, dus
 * de RF-machinerie zou alleen overhead zijn.
 *
 * Inhoud:
 * - Slot-grid uit `template.grid` + `template.slots`
 * - Per slot: bold name-heading + italic «stereotype»-subtitle, daarna
 *   `▸ <label>`-regels per concept (gesorteerd op
 *   `viewState[id].orderInSlot`) + empty-placeholder
 * - Drop-handler per slot met `allowed_stereotypes`-validatie
 * - "Unassigned"-strook onderaan voor concepts met `position` maar zonder
 *   `slotId` (legacy data uit pre-Pakket-B `bmc.canvas`-instances)
 *
 * Render-stijl matcht `docs/architecture/bmc-canvas-voorbeeld.svg`:
 * geen card-bg per concept, alleen tekst-regels onder de slot-heading.
 * Per slot een eigen border + text-color afgeleid van de slot's fill
 * (Osterwalder-tinten) via `SLOT_PALETTE`.
 */
import type { Concept } from "@ae/domain";
import { type DragEvent, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getRegistry, resolveBilingual, resolveLabelForViewKind } from "../../registry";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { confirmHardDelete } from "../cascadeConfirm";
import { readDroppedTypeName } from "../palette/dragTypeName";

/**
 * Slot-paletten conform `docs/architecture/bmc-canvas-voorbeeld.svg` —
 * per soft Osterwalder-tint (slot.slot_color) horen een bijbehorende
 * border-stroke en text-color. De pack-file levert alleen `slot_color`
 * (de fill); wij koppelen 'm hier aan z'n stroke + text. Toekomstige
 * canvas-templates (Lean Canvas / SWOT / VPC) kunnen dezelfde pattern
 * volgen; bij een onbekende fill valt 't terug op de defaults.
 */
const SLOT_PALETTE: Record<string, { border: string; text: string }> = {
  "#F4F0E8": { border: "#888070", text: "#4A4438" }, // Key Partnerships
  "#FFF7E0": { border: "#B89540", text: "#6B5018" }, // Key Activities / Resources
  "#FFEBCC": { border: "#C58A40", text: "#6F4514" }, // Value Propositions
  "#E8F0F8": { border: "#6A8FB0", text: "#2C4A60" }, // Customer Relationships / Channels
  "#D6E8F2": { border: "#5A7E9A", text: "#1F3D52" }, // Customer Segments
  "#FCE8E8": { border: "#B05050", text: "#5C2020" }, // Cost Structure
  "#E0F0E0": { border: "#5A8F5A", text: "#1F4A1F" }, // Revenue Streams
};
const DEFAULT_SLOT_PALETTE = { border: "#cbd5e1", text: "#334155" };

interface CanvasGridLayoutProps {
  workspaceId: string;
  branchId: string;
}

interface SlotEntry {
  instanceId: string;
  concept: Concept;
  orderInSlot: number;
}

interface UnassignedEntry {
  instanceId: string;
  concept: Concept;
}

export function CanvasGridLayout({ workspaceId, branchId }: CanvasGridLayoutProps) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;

  // Re-derive op elke store-mutatie. Light-weight: één diagram, ~9
  // slots × handvol concepts.
  const revision = useWorkspaceData((s) => s.revision);
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const lastError = useWorkspaceData((s) => s.lastError);
  const selectedConceptId = useUi((s) => s.selectedConceptId);

  const data = useMemo(() => {
    void revision;
    if (!activeDiagramId) return null;
    const store = useWorkspaceData.getState().store;
    const reg = getRegistry();
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return null;
    const viewKind = reg.getViewKind(diagram.viewKind);
    if (!viewKind?.canvas_template_id) return null;
    const template = reg.getCanvasTemplate(viewKind.canvas_template_id);
    if (!template) return null;

    const slotMap = new Map<string, SlotEntry[]>();
    const unassigned: UnassignedEntry[] = [];

    for (const [key, ve] of Object.entries(diagram.viewState)) {
      const conceptId = ve.ref ?? key;
      const concept = store.getConcept(conceptId);
      if (!concept || concept.tombstoneInBranch) continue;
      // Filter relation-only entries (geen position én geen slotId).
      if (ve.slotId === undefined && ve.position === undefined) continue;
      if (ve.hidden === true) continue;

      if (ve.slotId !== undefined) {
        const arr = slotMap.get(ve.slotId) ?? [];
        arr.push({ instanceId: key, concept, orderInSlot: ve.orderInSlot ?? 0 });
        slotMap.set(ve.slotId, arr);
      } else if (ve.position !== undefined) {
        unassigned.push({ instanceId: key, concept });
      }
    }
    for (const arr of slotMap.values()) {
      arr.sort((a, b) => a.orderInSlot - b.orderInSlot);
    }

    return { diagram, viewKind, template, slotMap, unassigned };
  }, [revision, activeDiagramId]);

  // Delete / Cmd+Delete handler — consistent met free-form canvas
  // (Canvas.tsx ~regel 2670). DEL = soft-hide via hideConceptOnDiagram
  // (entry-key verdwijnt uit viewState, concept blijft in workspace).
  // Cmd/Ctrl/Alt+DEL = hard-delete domain-concept via removeBatch met
  // confirm-popup voor cascade-impact.
  //
  // Werkt op selectedInstanceId (entry-key van de geselecteerde
  // concept-instance) — zelfde state-bron als de visuele ring-highlight
  // op de ConceptRow.
  useEffect(() => {
    if (!workspaceId || !branchId) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Delete" && event.key !== "Backspace") return;
      if (useSession.getState().readOnly) return;
      // Geen delete vanuit een input/textarea/contenteditable — daar
      // is Backspace karakter-verwijderen.
      const target = event.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        if (target.isContentEditable) return;
      }
      const ui = useUi.getState();
      const selectedInstanceId = ui.selectedInstanceId;
      const selectedConceptId = ui.selectedConceptId;
      if (!selectedInstanceId || !selectedConceptId) return;
      event.preventDefault();
      event.stopPropagation();
      const hard = event.metaKey || event.ctrlKey || event.altKey;
      const ws = useWorkspaceData.getState();
      if (hard) {
        void (async () => {
          const langKey: "nl" | "en" = lang === "en" ? "en" : "nl";
          const ok = await confirmHardDelete({
            workspaceId,
            branchId,
            conceptIds: [selectedConceptId],
            lang: langKey,
            standaloneEdgeCount: 0,
            conceptName: (id) => ws.store.getConcept(id)?.name,
          });
          if (!ok) return;
          await ws.removeBatch(workspaceId, branchId, [selectedConceptId], []);
          // Wis de selectie na delete zodat een volgende DEL geen
          // restant-instance probeert te verwijderen.
          useUi.setState({ selectedConceptId: null, selectedInstanceId: null });
        })();
      } else {
        ws.hideConceptOnDiagram(workspaceId, branchId, selectedInstanceId);
        useUi.setState({ selectedConceptId: null, selectedInstanceId: null });
      }
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [workspaceId, branchId, lang]);

  const conceptAllowedInSlot = useCallback(
    (concept: Concept, allowed: readonly string[]): boolean => {
      const stereos = concept.stereotypes ?? [];
      return stereos.some((s) => allowed.includes(s));
    },
    [],
  );

  const handleDropOnSlot = useCallback(
    (event: DragEvent<HTMLDivElement>, slotId: string, allowed: readonly string[]) => {
      event.preventDefault();
      event.stopPropagation();
      if (!workspaceId || !branchId) return;
      if (useSession.getState().readOnly) return;
      const ws = useWorkspaceData.getState();
      const diagramId = ws.activeDiagramId;
      if (!diagramId) return;
      const diagram = ws.store.getDiagram(diagramId);
      if (!diagram) return;

      // Bereken de orderInSlot voor de nieuwe entry: één meer dan het
      // huidig hoogste, zodat 'ie onderaan in de stack landt.
      const currentMax = Object.values(diagram.viewState)
        .filter((ve) => ve.slotId === slotId)
        .reduce<number>((m, ve) => Math.max(m, ve.orderInSlot ?? 0), -1);
      const nextOrder = currentMax + 1;

      // Source 1: bestaand concept (drag uit Explorer of binnen-canvas).
      const existingId = event.dataTransfer.getData("application/ae-existing-concept-id");
      if (existingId) {
        const concept = ws.store.getConcept(existingId);
        if (!concept) return;
        if (!conceptAllowedInSlot(concept, allowed)) {
          useWorkspaceData.setState({
            lastError: rejectMessage(slotId, allowed, lang, t),
          });
          return;
        }
        ws.placeConceptInSlot(workspaceId, branchId, existingId, slotId, nextOrder);
        return;
      }

      // Source 2: bulk-drag (multi-select uit Explorer) — JSON array.
      const bulkRaw = event.dataTransfer.getData("application/ae-existing-concept-ids");
      if (bulkRaw) {
        let ids: string[] = [];
        try {
          const parsed = JSON.parse(bulkRaw);
          if (Array.isArray(parsed)) ids = parsed.filter((v): v is string => typeof v === "string");
        } catch {
          ids = [];
        }
        let placed = 0;
        let skipped = 0;
        for (const id of ids) {
          const concept = ws.store.getConcept(id);
          if (!concept) continue;
          if (!conceptAllowedInSlot(concept, allowed)) {
            skipped += 1;
            continue;
          }
          ws.placeConceptInSlot(workspaceId, branchId, id, slotId, nextOrder + placed);
          placed += 1;
        }
        if (skipped > 0) {
          useWorkspaceData.setState({
            lastError: t("canvas.bmc.dropSkipped", { count: skipped }),
          });
        }
        return;
      }

      // Source 3: palette-drop (nieuw concept).
      const typeId = event.dataTransfer.getData("application/ae-type-id");
      if (!typeId) return;
      const droppedName = readDroppedTypeName(event.dataTransfer);

      let defaultStereotypes: string[] | undefined;
      const stereoRaw = event.dataTransfer.getData("application/ae-default-stereotypes");
      if (stereoRaw) {
        try {
          const parsed = JSON.parse(stereoRaw);
          if (Array.isArray(parsed) && parsed.every((s): s is string => typeof s === "string")) {
            defaultStereotypes = parsed;
          }
        } catch {
          // ignore malformed payload
        }
      }
      // Slot-validatie pre-create — anders maakt-ie eerst een concept en
      // wordt 't dan alsnog geweigerd.
      if (!defaultStereotypes?.some((s) => allowed.includes(s))) {
        useWorkspaceData.setState({
          lastError: rejectMessage(slotId, allowed, lang, t),
        });
        return;
      }

      let defaultAttrs: Record<string, unknown> | undefined;
      const attrsRaw = event.dataTransfer.getData("application/ae-default-attributes");
      if (attrsRaw) {
        try {
          defaultAttrs = JSON.parse(attrsRaw) as Record<string, unknown>;
        } catch {
          // ignore
        }
      }

      const reg = getRegistry();
      const elementType = reg.getElementType(typeId);
      const initialName: { nl: string; en: string } = droppedName
        ? droppedName
        : elementType?.label
          ? { nl: elementType.label.nl ?? "", en: elementType.label.en ?? "" }
          : { nl: "Nieuw concept", en: "New concept" };

      void ws
        .addConceptOnCanvas(
          workspaceId,
          branchId,
          typeId,
          initialName,
          { x: 0, y: 0 }, // dummy; meteen overschreven door placeConceptInSlot
          defaultAttrs,
          defaultStereotypes,
        )
        .then((created) => {
          if (!created) return;
          useWorkspaceData
            .getState()
            .placeConceptInSlot(workspaceId, branchId, created.id, slotId, nextOrder);
        });
    },
    [workspaceId, branchId, conceptAllowedInSlot, lang, t],
  );

  const handleMoveOrder = useCallback(
    (slotId: string, instanceId: string, direction: "up" | "down") => {
      if (!workspaceId || !branchId) return;
      if (useSession.getState().readOnly) return;
      const ws = useWorkspaceData.getState();
      const diagramId = ws.activeDiagramId;
      if (!diagramId) return;
      const diagram = ws.store.getDiagram(diagramId);
      if (!diagram) return;
      // Verzamel alle entries in dit slot, gesorteerd op huidige order.
      const entries: Array<{ id: string; order: number }> = [];
      for (const [key, ve] of Object.entries(diagram.viewState)) {
        if (ve.slotId !== slotId) continue;
        entries.push({ id: key, order: ve.orderInSlot ?? 0 });
      }
      entries.sort((a, b) => a.order - b.order);
      const idx = entries.findIndex((e) => e.id === instanceId);
      if (idx === -1) return;
      const swapWith = direction === "up" ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= entries.length) return;
      // Re-nummer alle entries (0..N-1) na swap zodat order's
      // dense en deterministisch blijven.
      const swapped = entries.slice();
      [swapped[idx], swapped[swapWith]] = [
        swapped[swapWith] as { id: string; order: number },
        swapped[idx] as { id: string; order: number },
      ];
      for (let i = 0; i < swapped.length; i++) {
        const entry = swapped[i];
        if (!entry) continue;
        ws.placeConceptInSlot(workspaceId, branchId, entry.id, slotId, i);
      }
    },
    [workspaceId, branchId],
  );

  if (!data) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        {t("canvas.bmc.noTemplate")}
      </div>
    );
  }

  const { template, viewKind, slotMap, unassigned } = data;
  const gridTemplateRows =
    template.grid.row_heights?.join(" ") ?? `repeat(${template.grid.rows}, minmax(0, 1fr))`;
  const gridTemplateColumns =
    template.grid.column_widths?.join(" ") ?? `repeat(${template.grid.columns}, minmax(0, 1fr))`;

  return (
    <div className="relative flex h-full w-full flex-col bg-slate-50">
      <div className="flex-1 overflow-auto p-4">
        <div
          className="grid h-full min-h-[480px] gap-2"
          style={{ gridTemplateRows, gridTemplateColumns }}
        >
          {template.slots.map((slot) => {
            const entries = slotMap.get(slot.id) ?? [];
            const slotName = resolveBilingual(slot.name, lang);
            const palette = SLOT_PALETTE[slot.slot_color ?? ""] ?? DEFAULT_SLOT_PALETTE;
            const stereotypeLabel = resolveSlotStereotypeLabel(slot.allowed_stereotypes, lang);
            return (
              // biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel
              <div
                key={slot.id}
                className="flex min-h-[80px] flex-col overflow-hidden rounded-md"
                style={{
                  gridRow: `${slot.grid_row} / span ${slot.row_span ?? 1}`,
                  gridColumn: `${slot.grid_column} / span ${slot.col_span ?? 1}`,
                  background: slot.slot_color ?? "#ffffff",
                  border: `1.2px solid ${palette.border}`,
                  color: palette.text,
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "copy";
                }}
                onDrop={(e) => handleDropOnSlot(e, slot.id, slot.allowed_stereotypes)}
              >
                <div className="px-3 pt-2 pb-1">
                  <div className="font-bold text-[15px] leading-tight">{slotName}</div>
                  {stereotypeLabel && (
                    <div className="text-[11px] italic leading-tight opacity-80">
                      {stereotypeLabel}
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col gap-0.5 overflow-auto px-3 pt-1 pb-2">
                  {entries.length === 0 ? (
                    <EmptyPlaceholder allowed={slot.allowed_stereotypes} lang={lang} />
                  ) : (
                    entries.map((entry, idx) => (
                      <ConceptRow
                        key={entry.instanceId}
                        instanceId={entry.instanceId}
                        concept={entry.concept}
                        viewKindId={viewKind.id}
                        lang={lang}
                        isSelected={selectedConceptId === entry.concept.id}
                        canMoveUp={idx > 0}
                        canMoveDown={idx < entries.length - 1}
                        onSelect={() => {
                          useUi.setState({
                            selectedConceptId: entry.concept.id,
                            selectedRelationId: null,
                            selectedInstanceId: entry.instanceId,
                          });
                        }}
                        onOpen={() => {
                          useUi.setState({
                            selectedConceptId: entry.concept.id,
                            selectedRelationId: null,
                            selectedInstanceId: entry.instanceId,
                          });
                          useUi.getState().setAttributePanelOpen(true);
                        }}
                        onMoveUp={() => handleMoveOrder(slot.id, entry.instanceId, "up")}
                        onMoveDown={() => handleMoveOrder(slot.id, entry.instanceId, "down")}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {unassigned.length > 0 && (
          <div className="mt-3 rounded-md border border-amber-300 border-dashed bg-amber-50/60 p-2">
            <div className="mb-1 px-1 font-medium text-xs text-amber-800 uppercase tracking-wide">
              {t("canvas.bmc.unassigned")}
            </div>
            <div className="flex flex-wrap gap-1">
              {unassigned.map((entry) => (
                <UnassignedCard
                  key={entry.instanceId}
                  instanceId={entry.instanceId}
                  concept={entry.concept}
                  viewKindId={viewKind.id}
                  lang={lang}
                  isSelected={selectedConceptId === entry.concept.id}
                  onSelect={() => {
                    useUi.setState({
                      selectedConceptId: entry.concept.id,
                      selectedRelationId: null,
                      selectedInstanceId: entry.instanceId,
                    });
                  }}
                  onOpen={() => {
                    useUi.setState({
                      selectedConceptId: entry.concept.id,
                      selectedRelationId: null,
                      selectedInstanceId: entry.instanceId,
                    });
                    useUi.getState().setAttributePanelOpen(true);
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {lastError && (
        <div className="absolute top-3 right-3 max-w-md rounded border border-rose-300 bg-rose-50 px-3 py-2 text-rose-900 text-sm shadow">
          <div className="flex items-start gap-2">
            <span>{lastError}</span>
            <button
              type="button"
              className="text-rose-700 hover:underline"
              onClick={() => useWorkspaceData.getState().clearError()}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface ConceptRowProps {
  instanceId: string;
  concept: Concept;
  viewKindId: string;
  lang: string;
  isSelected: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onSelect: () => void;
  onOpen: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

/**
 * Eén regel per concept in een slot, conform `bmc-canvas-voorbeeld.svg`:
 * "▸ <label>" tekst-stijl in de slot's text-color, geen card-bg/border.
 * Selectie geeft een subtiele highlight; ▲▼-knoppen verschijnen op
 * hover voor stack-herordening (geen onderdeel van de mockup, wel
 * modeleer-ergonomie). Drag-handle voor cross-slot move/copy.
 */
function ConceptRow({
  instanceId,
  concept,
  viewKindId,
  lang,
  isSelected,
  canMoveUp,
  canMoveDown,
  onSelect,
  onOpen,
  onMoveUp,
  onMoveDown,
}: ConceptRowProps) {
  const { t } = useTranslation();
  const label = resolveLabelForViewKind(concept, viewKindId, lang);
  // Alleen kijken: niet verslepen, niet herordenen.
  const readOnly = useSession((s) => s.readOnly);

  const onDragStart = (e: DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData("application/ae-existing-concept-id", concept.id);
    e.dataTransfer.effectAllowed = "copyMove";
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel
    <div
      draggable={!readOnly}
      onDragStart={onDragStart}
      onClick={onSelect}
      onDoubleClick={onOpen}
      className={`group flex cursor-pointer items-start gap-1 rounded-sm px-1 py-0.5 text-xs leading-tight transition-colors hover:bg-black/5 ${
        isSelected ? "bg-black/10 ring-1 ring-blue-500/60" : ""
      }`}
      data-instance-id={instanceId}
    >
      <span className="inline-block w-5 select-none text-lg leading-none opacity-70" aria-hidden>
        ▸
      </span>
      <span className="min-w-0 flex-1 truncate">{label || "(unnamed)"}</span>
      <span
        hidden={readOnly}
        className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100"
      >
        <button
          type="button"
          disabled={!canMoveUp}
          onClick={(e) => {
            e.stopPropagation();
            onMoveUp();
          }}
          className="px-0.5 text-[9px] disabled:cursor-not-allowed disabled:opacity-30"
          title={t("canvas.bmc.moveUp")}
        >
          ▲
        </button>
        <button
          type="button"
          disabled={!canMoveDown}
          onClick={(e) => {
            e.stopPropagation();
            onMoveDown();
          }}
          className="px-0.5 text-[9px] disabled:cursor-not-allowed disabled:opacity-30"
          title={t("canvas.bmc.moveDown")}
        >
          ▼
        </button>
      </span>
    </div>
  );
}

interface UnassignedCardProps {
  instanceId: string;
  concept: Concept;
  viewKindId: string;
  lang: string;
  isSelected: boolean;
  onSelect: () => void;
  onOpen: () => void;
}

/**
 * Pre-Pakket-B-instances zonder `slotId` maar mét `position` belanden
 * in de strook onderaan. Render-stijl matcht de slot-rows: ▸ + label,
 * compact en draggable zodat de modelleur 'm naar een passend slot kan
 * slepen.
 */
function UnassignedCard({
  instanceId,
  concept,
  viewKindId,
  lang,
  isSelected,
  onSelect,
  onOpen,
}: UnassignedCardProps) {
  const label = resolveLabelForViewKind(concept, viewKindId, lang);
  const readOnly = useSession((s) => s.readOnly);

  const onDragStart = (e: DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData("application/ae-existing-concept-id", concept.id);
    e.dataTransfer.effectAllowed = "copyMove";
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel
    <div
      draggable={!readOnly}
      onDragStart={onDragStart}
      onClick={onSelect}
      onDoubleClick={onOpen}
      className={`flex cursor-pointer items-center gap-1 rounded-sm px-2 py-0.5 text-xs text-amber-900 hover:bg-amber-100 ${
        isSelected ? "bg-amber-200" : ""
      }`}
      data-instance-id={instanceId}
      title={label}
    >
      <span className="inline-block w-5 text-lg leading-none opacity-70" aria-hidden>
        ▸
      </span>
      <span className="truncate">{label || "(unnamed)"}</span>
    </div>
  );
}

function EmptyPlaceholder({ allowed, lang }: { allowed: readonly string[]; lang: string }) {
  const reg = getRegistry();
  // Pak de label van het eerste toegestane stereotype als hint.
  const firstAllowed = allowed[0];
  const stereotype = firstAllowed ? reg.getStereotype(firstAllowed) : undefined;
  const label = stereotype ? resolveBilingual(stereotype.label, lang) : (firstAllowed ?? "?");
  return (
    <div className="flex flex-1 items-center justify-center text-xs italic opacity-60">
      + {label}
    </div>
  );
}

/**
 * Slot-stereotype subtitle (bv. `«bmc.KeyPartnership»`). Pakt het
 * eerste toegestane stereotype en wikkelt 't in guillemets als 't
 * label dat nog niet doet. Returnt null wanneer geen stereotype
 * geregistreerd — voorkomt een lege italic regel.
 */
function resolveSlotStereotypeLabel(allowed: readonly string[], lang: string): string | null {
  const reg = getRegistry();
  const first = allowed[0];
  if (!first) return null;
  const st = reg.getStereotype(first);
  const raw = st ? resolveBilingual(st.label, lang) : first;
  if (!raw) return null;
  // Stereotype-labels hebben in onze packs vaak al «...»-wrapping;
  // sommige stripten in M5 die brackets weg. Defense-in-depth.
  if (raw.startsWith("«") && raw.endsWith("»")) return raw;
  return `«${raw}»`;
}

function rejectMessage(
  slotId: string,
  allowed: readonly string[],
  lang: string,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  const reg = getRegistry();
  const labels = allowed.map((s) => {
    const st = reg.getStereotype(s);
    return st ? resolveBilingual(st.label, lang) : s;
  });
  return t("canvas.bmc.dropRejected", {
    slot: slotId,
    allowed: labels.join(", "),
  });
}
